import type { AQISample, EnvironmentalSummary, DifficultyFactor } from '@/types/running';

interface DifficultyInput {
  aqi_samples: AQISample[];
  elevation_gain: number;
  distance_meters: number;
}

const avg = (arr: number[]) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

export function calculateEnvironmentalSummary(input: DifficultyInput): EnvironmentalSummary {
  const { aqi_samples, elevation_gain } = input;

  const temps = aqi_samples.map(s => s.temperature).filter((t): t is number => t !== null);
  const humids = aqi_samples.map(s => s.humidity).filter((h): h is number => h !== null);
  const uvs = aqi_samples.map(s => s.uv_index).filter((u): u is number => u !== null && u !== undefined);
  const aqis = aqi_samples.map(s => s.aqi);

  const avgTemp = temps.length ? avg(temps) : null;
  const avgHumid = humids.length ? avg(humids) : null;
  const avgUV = uvs.length ? avg(uvs) : null;
  const avgAQI = aqis.length ? avg(aqis) : 0;

  // Sun exposure: % of samples where UV > 3
  const highUVSamples = uvs.filter(u => u > 3).length;
  const sunExposurePct = uvs.length ? Math.round((highUVSamples / uvs.length) * 100) : 0;

  const factors: DifficultyFactor[] = [];
  let totalScore = 0;

  // 1. Temperature (0-2.5) — optimal 10-18C
  if (avgTemp !== null) {
    let p = 0;
    if (avgTemp < 0) p = 2.5;
    else if (avgTemp < 5) p = 1.8;
    else if (avgTemp < 10) p = 0.8;
    else if (avgTemp <= 18) p = 0;
    else if (avgTemp <= 25) p = 0.5;
    else if (avgTemp <= 30) p = 1.2;
    else if (avgTemp <= 35) p = 2.0;
    else p = 2.5;
    totalScore += p;
    if (p > 0.3) factors.push({ factor: 'temperature', contribution: p / 10, label: `${avgTemp.toFixed(0)}\u00B0C ${avgTemp > 25 ? '(Hot)' : '(Cold)'}` });
  }

  // 2. Humidity (0-2.0) — high humidity = harder
  if (avgHumid !== null) {
    let p = 0;
    if (avgHumid > 85) p = 2.0;
    else if (avgHumid > 70) p = 1.2;
    else if (avgHumid > 60) p = 0.5;
    // Heat + humidity compound effect
    if (avgTemp !== null && avgTemp > 25 && avgHumid > 60) p = Math.min(2.0, p * 1.5);
    totalScore += p;
    if (p > 0.3) factors.push({ factor: 'humidity', contribution: p / 10, label: `${avgHumid.toFixed(0)}% humidity` });
  }

  // 3. AQI (0-2.5)
  if (avgAQI > 0) {
    let p = 0;
    if (avgAQI <= 50) p = 0;
    else if (avgAQI <= 100) p = 0.8;
    else if (avgAQI <= 150) p = 1.5;
    else if (avgAQI <= 200) p = 2.0;
    else p = 2.5;
    totalScore += p;
    if (p > 0.3) factors.push({ factor: 'aqi', contribution: p / 10, label: `AQI ${Math.round(avgAQI)}` });
  }

  // 4. Elevation (0-2.0) — gain per km
  const distKm = input.distance_meters / 1000;
  const gainPerKm = distKm > 0 ? elevation_gain / distKm : 0;
  let elevP = 0;
  if (gainPerKm > 80) elevP = 2.0;
  else if (gainPerKm > 50) elevP = 1.5;
  else if (gainPerKm > 30) elevP = 1.0;
  else if (gainPerKm > 15) elevP = 0.5;
  totalScore += elevP;
  if (elevP > 0.3) factors.push({ factor: 'elevation', contribution: elevP / 10, label: `${elevation_gain.toFixed(0)}m gain` });

  // 5. UV (0-1.0)
  if (avgUV !== null && avgUV > 3) {
    let p = 0;
    if (avgUV > 8) p = 1.0;
    else if (avgUV > 6) p = 0.7;
    else p = 0.3;
    totalScore += p;
    if (p > 0.2) factors.push({ factor: 'uv', contribution: p / 10, label: `UV ${avgUV.toFixed(1)}` });
  }

  const difficultyScore = Math.min(10, Math.max(0, Math.round(totalScore * 10) / 10));
  const percentile = estimatePercentile(difficultyScore);

  return {
    avg_temperature: avgTemp,
    min_temperature: temps.length ? Math.min(...temps) : null,
    max_temperature: temps.length ? Math.max(...temps) : null,
    avg_humidity: avgHumid,
    avg_uv_index: avgUV,
    max_uv_index: uvs.length ? Math.max(...uvs) : null,
    sun_exposure_pct: sunExposurePct,
    difficulty_score: difficultyScore,
    difficulty_percentile: percentile,
    difficulty_factors: factors,
  };
}

function estimatePercentile(score: number): number {
  if (score <= 1) return 10;
  if (score <= 2) return 25;
  if (score <= 3) return 40;
  if (score <= 4) return 55;
  if (score <= 5) return 65;
  if (score <= 6) return 75;
  if (score <= 7) return 85;
  if (score <= 8) return 92;
  if (score <= 9) return 97;
  return 99;
}
